import { useMemo, useState } from 'react';
import {
  AdministrationRoles,
  BillingRoles,
  CalendarRoles,
  DirectoryRoles,
  RulesEngineRoles,
  ToolsRoles,
  UserRoles,
  VaultRoles,
} from '../utils/roles/client-roles';
import { GroupHeader, GroupItems } from './autocomplete-groups-ui';
import { useUserSettings } from '../utils/use-user-settings';
import { isFeatureEnabled } from '../utils/feature-flags';
import { getVisibleDriveRoles } from '../utils/roles/visible-drive-roles';

import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Visibility from '@mui/icons-material/Visibility';

interface RolesAutoCompleteProps {
  setAutoCompleteValue: (value: string) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  hasError?: boolean;
  newKey?: string;
  helperText?: string;
  onSelection?: (value: string) => void;
}

export default function RolesAutoComplete({
  setAutoCompleteValue,
  size,
  disabled,
  hasError,
  newKey,
  helperText,
  onSelection,
}: RolesAutoCompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const { features } = useUserSettings();

  const key = useMemo(
    () => newKey || new Date().getTime().toString(),
    [newKey]
  );

  const isGroupsGovernanceFeatureEnabled = isFeatureEnabled(
    'groupsGovernanceBackend',
    features
  );
  const isDriveLabelsFeatureEnabled = isFeatureEnabled(
    'driveLabelsRead',
    features
  );
  const isSlackIntegrationFeatureEnabled = isFeatureEnabled(
    'slackIntegration',
    features
  );
  const isVaultExportFeatureEnabled = isFeatureEnabled(
    'vaultExportReact',
    features
  );

  const visibleUserRoles = isGroupsGovernanceFeatureEnabled
    ? UserRoles
    : (Object.fromEntries(
        Object.entries(UserRoles).filter(([key]) => key !== 'ViewUserRolesTab')
      ) as typeof UserRoles);

  const visibleDirectoryRoles = isGroupsGovernanceFeatureEnabled
    ? DirectoryRoles
    : (Object.fromEntries(
        Object.entries(DirectoryRoles).filter(
          ([key]) => key !== 'EditGroupHealthSettings'
        )
      ) as typeof DirectoryRoles);

  const visibleToolsRoles = Object.fromEntries(
    Object.entries(ToolsRoles).filter(
      ([key]) => key !== 'RunExportAdminLogsReport'
    )
  ) as typeof ToolsRoles;

  const visibleDriveRoles = getVisibleDriveRoles(features);

  let visibleAdministrationRoles = isDriveLabelsFeatureEnabled
    ? AdministrationRoles
    : (Object.fromEntries(
        Object.entries(AdministrationRoles).filter(
          ([key]) => key !== 'ViewLabelsManager'
        )
      ) as typeof AdministrationRoles);

  if (!isSlackIntegrationFeatureEnabled) {
    visibleAdministrationRoles = Object.fromEntries(
      Object.entries(visibleAdministrationRoles).filter(
        ([key]) =>
          !['ViewSlackIntegration', 'UpdateSlackIntegration'].includes(key)
      )
    ) as typeof AdministrationRoles;
  }

  const allRolesArray: {
    name: string;
    value: string;
    type: string;
    group: string;
  }[] = [];

  Object.keys(visibleDriveRoles).forEach((key) =>
    allRolesArray.push(visibleDriveRoles[key as keyof typeof visibleDriveRoles])
  );
  Object.keys(visibleAdministrationRoles).forEach((key) =>
    allRolesArray.push(
      visibleAdministrationRoles[key as keyof typeof visibleAdministrationRoles]
    )
  );
  Object.keys(CalendarRoles).forEach((key) =>
    allRolesArray.push(CalendarRoles[key as keyof typeof CalendarRoles])
  );
  Object.keys(RulesEngineRoles).forEach((key) =>
    allRolesArray.push(RulesEngineRoles[key as keyof typeof RulesEngineRoles])
  );
  Object.keys(visibleDirectoryRoles).forEach((key) =>
    allRolesArray.push(
      visibleDirectoryRoles[key as keyof typeof visibleDirectoryRoles]
    )
  );
  Object.keys(visibleToolsRoles).forEach((key) =>
    allRolesArray.push(visibleToolsRoles[key as keyof typeof visibleToolsRoles])
  );
  Object.keys(visibleUserRoles).forEach((key) =>
    allRolesArray.push(visibleUserRoles[key as keyof typeof visibleUserRoles])
  );
  Object.keys(BillingRoles).forEach((key) =>
    allRolesArray.push(BillingRoles[key as keyof typeof BillingRoles])
  );
  if (isVaultExportFeatureEnabled) {
    Object.keys(VaultRoles).forEach((key) =>
      allRolesArray.push(VaultRoles[key as keyof typeof VaultRoles])
    );
  }

  return (
    <Autocomplete
      key={key}
      id="roles-autocomplete"
      disabled={disabled}
      getOptionLabel={(option) => option.name}
      options={allRolesArray}
      size={size}
      autoComplete
      onChange={(_event, newValue, reason) => {
        let selectedValue = '';
        if (reason === 'clear') {
          setAutoCompleteValue('');
          if (onSelection) {
            onSelection('');
          }
          return;
        }
        selectedValue = newValue?.value || '';
        setAutoCompleteValue(selectedValue);
        if (
          selectedValue &&
          (reason === 'selectOption' || reason === 'createOption') &&
          onSelection
        ) {
          onSelection(selectedValue);
        }
      }}
      noOptionsText="No Roles Found"
      renderInput={(params) => (
        <TextField
          {...params}
          label="Permission Required"
          fullWidth
          error={hasError}
          helperText={helperText}
        />
      )}
      renderOption={(props, option) => {
        const { key: optionKey, ...optionProps } = props;
        return (
          <li key={optionKey} {...optionProps}>
            <Stack flexDirection="row" alignItems="center" gap={1}>
              {option.type === 'view' ? (
                <Visibility fontSize="small" color="primary" />
              ) : option.type === 'update' ? (
                <Edit fontSize="small" color="primary" />
              ) : option.type === 'delete' ? (
                <Delete fontSize="small" color="primary" />
              ) : (
                <AddCircleOutline fontSize="small" color="primary" />
              )}
              <Typography>{option.name}</Typography>
            </Stack>
          </li>
        );
      }}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      componentsProps={{
        paper: {
          sx: {
            minWidth: 300,
          },
        },
      }}
      groupBy={(option) => option.group}
      renderGroup={(params) => (
        <li key={params.key}>
          <GroupHeader>
            {params.group === 'userManagement'
              ? 'User Management'
              : params.group === 'directory'
              ? 'Directory'
              : params.group === 'drive'
              ? 'Drive'
              : params.group === 'calendars'
              ? 'Calendars'
              : params.group === 'rulesEngine'
              ? 'Rules Engine'
              : params.group === 'tools'
              ? 'Tools'
              : params.group === 'billing'
              ? 'Billing'
              : params.group === 'vault'
              ? 'Vault'
              : 'Administration'}
          </GroupHeader>
          <GroupItems>{params.children}</GroupItems>
        </li>
      )}
    />
  );
}
